import * as React from "react";

import { Button } from "@/lib/shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import { useAllApartments } from "@/store/slices/apartments";
import type { Address } from "@/models/responses";
import { useAddAddressMutation, useGetAddressesQuery, useGetMunicipalitySectionsQuery } from "@/store/booking-management-api";
import { findAddressNearMisses } from "@/utils/addressNearMiss";

/**
 * Registering a building without leaving the apartment form
 * (address-normalization/FR-011, /FR-012, /AS-012, /AS-013, /AS-014).
 *
 * Two things it is NOT:
 *
 *  - It is not a modal. It is a panel that opens in place beneath the address
 *    control, the same shape as `StrOwnerSelect`'s inline owner create. A Radix
 *    `Dialog` would put its own dismissable layer between the operator and the
 *    municipality-section combobox, whose list portals to the body — the classic
 *    combobox-inside-a-dialog trap — for no gain the operator can see.
 *  - Its near-miss check is not a duplicate check. Identity duplicates are the
 *    server's refusal (FR-003). This warns about buildings that merely READ
 *    alike, and it always leaves the last word with the operator, because a
 *    dropped `β` and `Αριστοτέλους 11` next door to `111` are the same shape of
 *    difference (EC-011).
 */

const DIALOG_TESTID = "address-create-dialog";
const STREET_ID = "address-create-street";
const NUMBER_ID = "address-create-number";
const POSTAL_ID = "address-create-postal-code";
const SECTION_TESTID = "address-create-municipality-section";
const SUBMIT_TESTID = "address-create-submit";
const ERROR_ID = "address-create-error";
const NEAR_MISS_TESTID = "address-near-miss-warning";
const NEAR_MISS_CONFIRM_TESTID = "address-near-miss-confirm";
const nearMissUseExistingTestId = (addressId: number): string => `address-near-miss-use-existing-${addressId}`;

export interface AddressCreateDialogProps {
    /** Closed without creating anything (Cancel). */
    onCancel: () => void;
    /**
     * A building was settled on — either the one just created, or the existing
     * one the operator recognised in the near-miss warning. Both leave the
     * apartment form with an address selected, which is all it asked for.
     */
    onAddressChosen: (address: Address) => void;
}

const POSTAL_CODE_PATTERN = /^\d{5}$/u;

function apartmentCountLabel(count: number): string {
    return count === 1 ? "1 apartment" : `${count} apartments`;
}

export const AddressCreateDialog: React.FC<AddressCreateDialogProps> = ({ onCancel, onAddressChosen }) => {
    // Both reads are already in the store: the apartment form loaded the
    // buildings and `AppDataProvider` loaded the apartments. Subscribing costs
    // no request, which is what makes the comparison below satisfy FR-013.
    const { data: existingAddresses = [] } = useGetAddressesQuery();
    const { data: municipalitySections = [] } = useGetMunicipalitySectionsQuery();
    const apartments = useAllApartments();
    const [addAddress] = useAddAddressMutation();

    const municipalitySectionOptions = React.useMemo(
        () => municipalitySections.map((section) => ({ value: String(section.id), label: section.name })),
        [municipalitySections],
    );

    const [street, setStreet] = React.useState("");
    const [number, setNumber] = React.useState("");
    const [postalCode, setPostalCode] = React.useState("");
    const [municipalitySectionId, setMunicipalitySectionId] = React.useState(0);
    const [nearMisses, setNearMisses] = React.useState<Address[] | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    // Opening this panel UNMOUNTS the "＋ Add a new address" button that opened
    // it, so without moving focus the keyboard falls back to `document.body` and
    // the `role="dialog"` below is announced to nobody (WCAG 2.4.3).
    const streetRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        streetRef.current?.focus();
    }, []);

    // The warning is the operator's next decision and both ways out of it live
    // inside it, so the keyboard goes to the region — not to one of its buttons,
    // which would name "Use this one" while swallowing the warning it answers.
    const nearMissRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (nearMisses !== null) {
            nearMissRef.current?.focus();
        }
    }, [nearMisses]);

    // How many apartments sit at each building, from the list the page already
    // holds. `Address` carries no count and fetching one would be the extra
    // request FR-013 forbids.
    const apartmentsByAddress = React.useMemo(() => {
        const counts = new Map<number, number>();
        apartments.forEach((apartment) => {
            if (apartment.addressId != null) {
                counts.set(apartment.addressId, (counts.get(apartment.addressId) ?? 0) + 1);
            }
        });
        return counts;
    }, [apartments]);

    const create = async (): Promise<void> => {
        try {
            const created = await addAddress({ street: street.trim(), number: number.trim(), postalCode: postalCode.trim(), municipalitySectionId }).unwrap();
            onAddressChosen(created);
        } catch {
            // The server owns the duplicate-identity refusal (FR-003) and the
            // field-level ones (EC-007, EC-013); surface them where the operator
            // is typing rather than in a toast they can dismiss and forget.
            setError("The address could not be created. Check the details and try again.");
        }
    };

    const handleSubmit = async (): Promise<void> => {
        const draft = { street: street.trim(), number: number.trim(), postalCode: postalCode.trim() };
        if (draft.street === "" || draft.number === "" || !POSTAL_CODE_PATTERN.test(draft.postalCode) || municipalitySectionId === 0) {
            setError("Street, number, a five-digit postal code and a municipality section are all required.");
            return;
        }
        setError(null);

        const candidates = findAddressNearMisses(draft, existingAddresses);
        if (candidates.length > 0) {
            setNearMisses(candidates);
            return;
        }
        await create();
    };

    return (
        <Card className="mt-2" role="dialog" aria-label="Add a new address" data-testid={DIALOG_TESTID}>
            <CardHeader>
                <CardTitle>New Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="space-y-2">
                    <Label htmlFor={STREET_ID}>Street*</Label>
                    <Input
                        id={STREET_ID}
                        ref={streetRef}
                        data-testid={STREET_ID}
                        value={street}
                        aria-required="true"
                        onChange={(e) => setStreet(e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={NUMBER_ID}>Number*</Label>
                    <Input id={NUMBER_ID} data-testid={NUMBER_ID} value={number} aria-required="true" onChange={(e) => setNumber(e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={POSTAL_ID}>Postal Code*</Label>
                    <Input id={POSTAL_ID} data-testid={POSTAL_ID} value={postalCode} aria-required="true" onChange={(e) => setPostalCode(e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={SECTION_TESTID}>Municipality Section*</Label>
                    <SearchableSelect
                        variant="outline"
                        width="w-full"
                        id={SECTION_TESTID}
                        data-testid={SECTION_TESTID}
                        // No aria-label: it would OVERRIDE the <Label htmlFor> above per
                        // accessible-name precedence, and the label is what carries the
                        // required marker. Naming it here would announce "Municipality
                        // Section" and silently drop the "*" a sighted operator can see.
                        aria-required={true}
                        placeholder="Select Municipality Section"
                        options={municipalitySectionOptions}
                        selectedValue={municipalitySectionId === 0 ? "" : String(municipalitySectionId)}
                        onChange={(value) => setMunicipalitySectionId(Number(value))}
                    />
                </div>

                {error && (
                    <p id={ERROR_ID} role="alert" className="text-sm text-destructive">
                        {error}
                    </p>
                )}

                {nearMisses && (
                    <div
                        data-testid={NEAR_MISS_TESTID}
                        ref={nearMissRef}
                        role="alert"
                        tabIndex={-1}
                        className="space-y-2 rounded-md border border-amber-500 p-3 text-sm"
                    >
                        <p>This closely resembles a building that is already registered. Is it one of these?</p>
                        <ul className="space-y-2">
                            {nearMisses.map((candidate) => (
                                <li key={candidate.id} className="flex items-center justify-between gap-2">
                                    <span>
                                        {candidate.canonical}
                                        {" — "}
                                        {apartmentCountLabel(apartmentsByAddress.get(candidate.id) ?? 0)}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        data-testid={nearMissUseExistingTestId(candidate.id)}
                                        onClick={() => onAddressChosen(candidate)}
                                    >
                                        Use this one
                                    </Button>
                                </li>
                            ))}
                        </ul>
                        <Button type="button" variant="volt" size="sm" data-testid={NEAR_MISS_CONFIRM_TESTID} onClick={() => void create()}>
                            No, this is a different building — create it
                        </Button>
                    </div>
                )}

                <div className="flex gap-2">
                    <Button type="button" data-testid={SUBMIT_TESTID} onClick={() => void handleSubmit()}>
                        Create Address
                    </Button>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default AddressCreateDialog;
